import React, { ChangeEvent, FC, useCallback, memo } from 'react';
import { FieldProps, Field, useFormikContext } from 'formik';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import Input from '@minecraft.input';
import { setFieldValueAndTouched } from '@minecraft.utils';
import { useTranslation } from '@blocs.i18n';
import { RangeEnum, FilterData, FilterValues } from '../../../shared/types';
import { useMeasure } from '../../../shared/utils';
import { FieldGroup, Delimiter, StyledCheckbox } from '../../../shared/styles';

type Props = {
  ageRangeName: FilterData['name'];
  switchAgeToMonthsName: FilterData['name'];
  switchAgeToMonthsLabel?: FilterData['label'];
  ageMinName: FilterData['name'];
  ageMaxName: FilterData['name'];
  label: FilterData['label'];
  ageLimit?: number;
  qaId?: string;
};

const AgeRange: FC<Props> = ({
  ageRangeName,
  switchAgeToMonthsName,
  switchAgeToMonthsLabel,
  ageMinName,
  ageMaxName,
  label,
  ageLimit,
  qaId,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const { t } = useTranslation();
  const labelMeasure = useMeasure({
    switchTo: values[ageRangeName][switchAgeToMonthsName],
    type: RangeEnum.age,
  });
  const toggleLabel = `${label} (${labelMeasure})`;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const currentInputName = event.currentTarget.name;
      const currentInputValue = event.currentTarget.value;
      const currentInputLimit = event.currentTarget.max;

      if (Number(currentInputValue) > Number(currentInputLimit)) {
        setFieldValue(currentInputName, currentInputLimit, false);
      } else {
        setFieldValue(currentInputName, currentInputValue, false);
      }
    },
    [setFieldValue]
  );

  return (
    <Field>
      {({ form }: FieldProps<FilterValues>): JSX.Element => {
        return (
          <DropdownWithChildren
            isChildrensContentFilled={form.values[ageRangeName][ageMinName] || form.values[ageRangeName][ageMaxName]}
            toggleLabel={toggleLabel}
            qaId={qaId}
          >
            <>
              <FieldGroup>
                <Input
                  name={`${ageRangeName}.${ageMinName}`}
                  value={form.values[ageRangeName][ageMinName]}
                  type="number"
                  size="small"
                  min={0}
                  max={ageLimit}
                  onChange={handleChange}
                  data-testid={`${ageRangeName}.${ageMinName}`}
                  data-qa-id={`${qaId}-${ageMinName}`}
                  aria-label={t('common:form.label.roleAgeRange.min')}
                />
                <Delimiter>{t('common:filter.to')}</Delimiter>
                <Input
                  name={`${ageRangeName}.${ageMaxName}`}
                  value={form.values[ageRangeName][ageMaxName]}
                  type="number"
                  size="small"
                  min={0}
                  max={ageLimit}
                  onChange={handleChange}
                  data-testid={`${ageRangeName}.${ageMaxName}`}
                  data-qa-id={`${qaId}-${ageMaxName}`}
                  aria-label={t('common:form.label.roleAgeRange.max')}
                />
              </FieldGroup>
              <StyledCheckbox
                isChecked={form.values[ageRangeName][switchAgeToMonthsName]}
                name={`${ageRangeName}.${switchAgeToMonthsName}`}
                label={switchAgeToMonthsLabel ?? t('common:filter.switchToMonths')}
                onChange={(): void => {
                  setFieldValueAndTouched(
                    `${ageRangeName}.${switchAgeToMonthsName}`,
                    !form.values[ageRangeName][switchAgeToMonthsName],
                    form
                  );
                }}
              />
            </>
          </DropdownWithChildren>
        );
      }}
    </Field>
  );
};

export default memo(AgeRange);
